import * as THREE from 'three';

/**
 * Manages the Three.js scene and its contents
 */
export class SceneManager {
  private scene: THREE.Scene;
  private objects: THREE.Object3D[] = [];
  private updateCallbacks: ((deltaTime: number) => void)[] = [];
  private textureLoader: THREE.TextureLoader;

  /**
   * Creates a new SceneManager instance
   * @param scene The Three.js scene to manage
   */
  constructor(scene: THREE.Scene) {
    this.scene = scene;
    this.textureLoader = new THREE.TextureLoader();
    this.setupBasicScene();
  }

  /**
   * Sets up a basic scene with a ground plane
   */
  private setupBasicScene(): void {
    // Load ground texture
    const grassTexture = this.textureLoader.load('/textures/grass.jpg');
    grassTexture.wrapS = THREE.RepeatWrapping;
    grassTexture.wrapT = THREE.RepeatWrapping;
    grassTexture.repeat.set(50, 50);
    
    // Create a ground plane with texture
    const groundGeometry = new THREE.PlaneGeometry(1000, 1000);
    const groundMaterial = new THREE.MeshStandardMaterial({
      map: grassTexture,
      roughness: 0.8,
      metalness: 0.2,
    });
    const ground = new THREE.Mesh(groundGeometry, groundMaterial);
    ground.rotation.x = -Math.PI / 2; // Rotate to be horizontal
    ground.position.y = -0.5;
    ground.receiveShadow = true;
    this.scene.add(ground);
    this.objects.push(ground);

    // Add a detailed castle
    this.addDetailedCastle();
  }

  /**
   * Adds a detailed castle with textured walls
   */
  private addDetailedCastle(): void {
    // Create castle container
    const castle = new THREE.Group();
    castle.position.set(0, 0, -20); // Position castle in the distance
    
    // Load textures
    const stoneTexture = this.textureLoader.load('/textures/stone_wall.jpg');
    stoneTexture.wrapS = THREE.RepeatWrapping;
    stoneTexture.wrapT = THREE.RepeatWrapping;
    
    const stoneBumpMap = this.textureLoader.load('/textures/stone_wall_bump.jpg');
    stoneBumpMap.wrapS = THREE.RepeatWrapping;
    stoneBumpMap.wrapT = THREE.RepeatWrapping;
    
    const roofTexture = this.textureLoader.load('/textures/roof_tiles.jpg');
    roofTexture.wrapS = THREE.RepeatWrapping;
    roofTexture.wrapT = THREE.RepeatWrapping;
    
    const woodTexture = this.textureLoader.load('/textures/wood.jpg');
    woodTexture.wrapS = THREE.RepeatWrapping;
    woodTexture.wrapT = THREE.RepeatWrapping;
    
    const waterTexture = this.textureLoader.load('/textures/water.jpg');
    waterTexture.wrapS = THREE.RepeatWrapping;
    waterTexture.wrapT = THREE.RepeatWrapping;
    waterTexture.repeat.set(10, 10);
    
    // Castle base materials with textures
    const stoneMaterial = new THREE.MeshStandardMaterial({ 
      map: stoneTexture,
      bumpMap: stoneBumpMap,
      bumpScale: 0.1,
      roughness: 0.7,
      metalness: 0.2
    });
    
    const darkStoneMaterial = new THREE.MeshStandardMaterial({ 
      map: stoneTexture,
      bumpMap: stoneBumpMap,
      bumpScale: 0.1,
      roughness: 0.8,
      metalness: 0.1,
      color: 0x999999 // Slightly darker tint
    });
    
    const woodMaterial = new THREE.MeshStandardMaterial({ 
      map: woodTexture,
      roughness: 0.9,
      metalness: 0.1
    });
    
    const roofMaterial = new THREE.MeshStandardMaterial({
      map: roofTexture,
      roughness: 0.7,
      metalness: 0.2
    });
    
    const waterMaterial = new THREE.MeshStandardMaterial({
      map: waterTexture,
      roughness: 0.2,
      metalness: 0.3,
      transparent: true,
      opacity: 0.8
    });
    
    // Create castle walls
    this.createCastleWalls(castle, stoneMaterial, darkStoneMaterial);
    
    // Create corner towers
    this.createCornerTowers(castle, stoneMaterial, darkStoneMaterial, roofMaterial);
    
    // Create main gate
    this.createMainGate(castle, stoneMaterial, woodMaterial, darkStoneMaterial);
    
    // Create central keep
    this.createKeep(castle, stoneMaterial, roofMaterial);
    
    // Add decorative elements
    this.addDecorativeElements(castle, stoneMaterial, woodMaterial, waterMaterial);
    
    // Add castle to scene
    this.scene.add(castle);
    this.objects.push(castle);
  }
  
  /**
   * Creates the castle walls with textured materials
   */
  private createCastleWalls(
    parent: THREE.Group, 
    stoneMaterial: THREE.Material, 
    darkStoneMaterial: THREE.Material
  ): void {
    // Wall dimensions
    const wallLength = 20;
    const wallHeight = 6;
    const wallThickness = 1;
    const battlementHeight = 1;
    const battlementWidth = 0.5;
    const battlementSpacing = 0.5;
    
    // Create walls
    const createWall = (x: number, z: number, rotationY: number) => {
      // Wall base
      const wallGeometry = new THREE.BoxGeometry(wallLength, wallHeight, wallThickness);
      
      // Adjust texture repeat based on wall dimensions
      if (stoneMaterial instanceof THREE.MeshStandardMaterial && stoneMaterial.map) {
        const texture = stoneMaterial.map.clone();
        texture.repeat.set(wallLength / 2, wallHeight / 2);
        
        // Create a new material with the adjusted texture
        const adjustedMaterial = stoneMaterial.clone();
        adjustedMaterial.map = texture;
        
        if (adjustedMaterial.bumpMap) {
          const bumpMap = adjustedMaterial.bumpMap.clone();
          bumpMap.repeat.set(wallLength / 2, wallHeight / 2);
          adjustedMaterial.bumpMap = bumpMap;
        }
        
        const wall = new THREE.Mesh(wallGeometry, adjustedMaterial);
        wall.position.set(x, wallHeight / 2, z);
        wall.rotation.y = rotationY;
        wall.castShadow = true;
        wall.receiveShadow = true;
        parent.add(wall);
        
        // Add battlements
        const battlementCount = Math.floor(wallLength / (battlementWidth + battlementSpacing));
        const battlementOffset = wallLength / 2 - battlementWidth / 2;
        
        for (let i = 0; i < battlementCount; i++) {
          const position = -battlementOffset + i * (battlementWidth + battlementSpacing);
          
          const battlementGeometry = new THREE.BoxGeometry(battlementWidth, battlementHeight, wallThickness);
          const battlement = new THREE.Mesh(battlementGeometry, darkStoneMaterial);
          
          // Position battlement on top of wall
          if (rotationY === 0 || rotationY === Math.PI) {
            battlement.position.set(position, wallHeight + battlementHeight / 2, 0);
          } else {
            battlement.position.set(0, wallHeight + battlementHeight / 2, position);
          }
          
          battlement.rotation.y = rotationY;
          battlement.castShadow = true;
          battlement.receiveShadow = true;
          wall.add(battlement);
        }
      } else {
        // Fallback if material isn't what we expect
        const wall = new THREE.Mesh(wallGeometry, stoneMaterial);
        wall.position.set(x, wallHeight / 2, z);
        wall.rotation.y = rotationY;
        wall.castShadow = true;
        wall.receiveShadow = true;
        parent.add(wall);
      }
    };
    
    // Create four walls
    createWall(0, wallLength / 2, 0); // North wall
    createWall(0, -wallLength / 2, Math.PI); // South wall
    createWall(-wallLength / 2, 0, Math.PI / 2); // West wall
    createWall(wallLength / 2, 0, -Math.PI / 2); // East wall
  }
  
  /**
   * Creates corner towers for the castle with textured materials
   */
  private createCornerTowers(
    parent: THREE.Group, 
    stoneMaterial: THREE.Material, 
    darkStoneMaterial: THREE.Material,
    roofMaterial: THREE.Material
  ): void {
    const towerRadius = 2;
    const towerHeight = 10;
    const wallLength = 20;
    const halfWall = wallLength / 2;
    
    // Create a tower
    const createTower = (x: number, z: number) => {
      // Tower base
      const towerGeometry = new THREE.CylinderGeometry(towerRadius, towerRadius * 1.2, towerHeight, 16);
      
      // Adjust texture for cylindrical surface
      if (stoneMaterial instanceof THREE.MeshStandardMaterial && stoneMaterial.map) {
        const texture = stoneMaterial.map.clone();
        texture.repeat.set(4, 4);
        
        const adjustedMaterial = stoneMaterial.clone();
        adjustedMaterial.map = texture;
        
        if (adjustedMaterial.bumpMap) {
          const bumpMap = adjustedMaterial.bumpMap.clone();
          bumpMap.repeat.set(4, 4);
          adjustedMaterial.bumpMap = bumpMap;
        }
        
        const tower = new THREE.Mesh(towerGeometry, adjustedMaterial);
        tower.position.set(x, towerHeight / 2, z);
        tower.castShadow = true;
        tower.receiveShadow = true;
        parent.add(tower);
        
        // Tower roof with texture
        const roofGeometry = new THREE.ConeGeometry(towerRadius + 0.5, 3, 16);
        const roof = new THREE.Mesh(roofGeometry, roofMaterial);
        roof.position.set(0, towerHeight / 2 + 1.5, 0);
        roof.castShadow = true;
        tower.add(roof);
        
        // Tower windows
        const windowCount = 4;
        const windowHeight = 1;
        const windowWidth = 0.5;
        const windowDepth = 0.3;
        
        for (let i = 0; i < windowCount; i++) {
          const angle = (i / windowCount) * Math.PI * 2;
          const windowX = Math.cos(angle) * (towerRadius - 0.1);
          const windowZ = Math.sin(angle) * (towerRadius - 0.1);
          
          const windowGeometry = new THREE.BoxGeometry(windowWidth, windowHeight, windowDepth);
          const window = new THREE.Mesh(windowGeometry, darkStoneMaterial);
          window.position.set(windowX, 1, windowZ);
          window.rotation.y = angle + Math.PI / 2;
          tower.add(window);
        }
      } else {
        // Fallback
        const tower = new THREE.Mesh(towerGeometry, stoneMaterial);
        tower.position.set(x, towerHeight / 2, z);
        tower.castShadow = true;
        tower.receiveShadow = true;
        parent.add(tower);
      }
    };
    
    // Create four corner towers
    createTower(-halfWall, -halfWall); // Southwest
    createTower(-halfWall, halfWall);  // Northwest
    createTower(halfWall, -halfWall);  // Southeast
    createTower(halfWall, halfWall);   // Northeast
  }
  
  /**
   * Creates the main gate for the castle with textured materials
   */
  private createMainGate(
    parent: THREE.Group, 
    stoneMaterial: THREE.Material, 
    woodMaterial: THREE.Material,
    darkStoneMaterial: THREE.Material
  ): void {
    const wallLength = 20;
    const gateWidth = 4;
    const gateHeight = 5;
    const wallThickness = 1;
    
    // Gate structure
    const gateGeometry = new THREE.BoxGeometry(gateWidth, gateHeight, wallThickness * 2);
    const gate = new THREE.Mesh(gateGeometry, stoneMaterial);
    gate.position.set(0, gateHeight / 2, -wallLength / 2);
    gate.castShadow = true;
    gate.receiveShadow = true;
    parent.add(gate);
    
    // Gate arch
    const archRadius = gateWidth / 2;
    const archSegments = 8;
    const archGeometry = new THREE.TorusGeometry(
      archRadius, 
      wallThickness / 2, 
      8, 
      archSegments, 
      Math.PI
    );
    const arch = new THREE.Mesh(archGeometry, darkStoneMaterial);
    arch.position.set(0, gateHeight - archRadius, -wallLength / 2);
    arch.rotation.x = Math.PI / 2;
    arch.castShadow = true;
    arch.receiveShadow = true;
    parent.add(arch);
    
    // Gate doors with wood texture
    const doorWidth = gateWidth / 2 - 0.1;
    const doorHeight = gateHeight - 0.5;
    const doorGeometry = new THREE.BoxGeometry(doorWidth, doorHeight, wallThickness / 4);
    
    // Adjust wood texture for doors
    if (woodMaterial instanceof THREE.MeshStandardMaterial && woodMaterial.map) {
      const texture = woodMaterial.map.clone();
      texture.repeat.set(1, 2);
      
      const adjustedWoodMaterial = woodMaterial.clone();
      adjustedWoodMaterial.map = texture;
      
      // Left door
      const leftDoor = new THREE.Mesh(doorGeometry, adjustedWoodMaterial);
      leftDoor.position.set(-doorWidth / 2, doorHeight / 2, -wallLength / 2 - 0.3);
      leftDoor.castShadow = true;
      leftDoor.receiveShadow = true;
      parent.add(leftDoor);
      
      // Right door
      const rightDoor = new THREE.Mesh(doorGeometry, adjustedWoodMaterial);
      rightDoor.position.set(doorWidth / 2, doorHeight / 2, -wallLength / 2 - 0.3);
      rightDoor.castShadow = true;
      rightDoor.receiveShadow = true;
      parent.add(rightDoor);
      
      // Door handles
      const handleGeometry = new THREE.SphereGeometry(0.15, 8, 8);
      const handleMaterial = new THREE.MeshStandardMaterial({ color: 0x222222 });
      
      const leftHandle = new THREE.Mesh(handleGeometry, handleMaterial);
      leftHandle.position.set(doorWidth / 4, 0, wallThickness / 4);
      leftDoor.add(leftHandle);
      
      const rightHandle = new THREE.Mesh(handleGeometry, handleMaterial);
      rightHandle.position.set(-doorWidth / 4, 0, wallThickness / 4);
      rightDoor.add(rightHandle);
    } else {
      // Fallback
      const leftDoor = new THREE.Mesh(doorGeometry, woodMaterial);
      leftDoor.position.set(-doorWidth / 2, doorHeight / 2, -wallLength / 2 - 0.3);
      leftDoor.castShadow = true;
      leftDoor.receiveShadow = true;
      parent.add(leftDoor);
      
      const rightDoor = new THREE.Mesh(doorGeometry, woodMaterial);
      rightDoor.position.set(doorWidth / 2, doorHeight / 2, -wallLength / 2 - 0.3);
      rightDoor.castShadow = true;
      rightDoor.receiveShadow = true;
      parent.add(rightDoor);
    }
  }
  
  /**
   * Creates the central keep (main tower) with textured materials
   */
  private createKeep(
    parent: THREE.Group, 
    stoneMaterial: THREE.Material, 
    roofMaterial: THREE.Material
  ): void {
    // Keep dimensions
    const keepWidth = 8;
    const keepDepth = 8;
    const keepHeight = 15;
    
    // Main keep structure
    const keepGeometry = new THREE.BoxGeometry(keepWidth, keepHeight, keepDepth);
    
    // Adjust texture for keep
    if (stoneMaterial instanceof THREE.MeshStandardMaterial && stoneMaterial.map) {
      const texture = stoneMaterial.map.clone();
      texture.repeat.set(keepWidth / 2, keepHeight / 2);
      
      const adjustedMaterial = stoneMaterial.clone();
      adjustedMaterial.map = texture;
      
      if (adjustedMaterial.bumpMap) {
        const bumpMap = adjustedMaterial.bumpMap.clone();
        bumpMap.repeat.set(keepWidth / 2, keepHeight / 2);
        adjustedMaterial.bumpMap = bumpMap;
      }
      
      const keep = new THREE.Mesh(keepGeometry, adjustedMaterial);
      keep.position.set(0, keepHeight / 2, 0);
      keep.castShadow = true;
      keep.receiveShadow = true;
      parent.add(keep);
      
      // Keep roof with texture
      const roofGeometry = new THREE.ConeGeometry(keepWidth / Math.sqrt(2) + 0.5, 5, 4);
      const roof = new THREE.Mesh(roofGeometry, roofMaterial);
      roof.position.set(0, keepHeight / 2 + 2.5, 0);
      roof.rotation.y = Math.PI / 4; // Rotate 45 degrees
      roof.castShadow = true;
      keep.add(roof);
      
      // Keep windows
      const windowHeight = 1.5;
      const windowWidth = 0.8;
      const windowDepth = 0.5;
      const windowY = 2;
      
      // Create windows on each side
      for (let i = 0; i < 4; i++) {
        const angle = (i / 4) * Math.PI * 2;
        const windowX = Math.sin(angle) * (keepWidth / 2);
        const windowZ = Math.cos(angle) * (keepDepth / 2);
        
        const windowGeometry = new THREE.BoxGeometry(windowWidth, windowHeight, windowDepth);
        const window = new THREE.Mesh(windowGeometry, new THREE.MeshBasicMaterial({ color: 0x000000 }));
        window.position.set(windowX, windowY, windowZ);
        window.rotation.y = angle;
        keep.add(window);
        
        // Add a second row of windows
        const window2 = window.clone();
        window2.position.y = windowY + 4;
        keep.add(window2);
      }
    } else {
      // Fallback
      const keep = new THREE.Mesh(keepGeometry, stoneMaterial);
      keep.position.set(0, keepHeight / 2, 0);
      keep.castShadow = true;
      keep.receiveShadow = true;
      parent.add(keep);
    }
  }
  
  /**
   * Adds decorative elements to the castle with textured materials
   */
  private addDecorativeElements(
    parent: THREE.Group, 
    stoneMaterial: THREE.Material, 
    woodMaterial: THREE.Material,
    waterMaterial: THREE.Material
  ): void {
    // Add flags
    const flagpoleHeight = 3;
    const flagpoleRadius = 0.05;
    const flagWidth = 1;
    const flagHeight = 0.8;
    
    const createFlag = (x: number, y: number, z: number, color: number) => {
      // Flagpole
      const poleGeometry = new THREE.CylinderGeometry(flagpoleRadius, flagpoleRadius, flagpoleHeight, 8);
      const pole = new THREE.Mesh(poleGeometry, woodMaterial);
      pole.position.set(x, y + flagpoleHeight / 2, z);
      pole.castShadow = true;
      parent.add(pole);
      
      // Flag with cloth-like material
      const flagGeometry = new THREE.PlaneGeometry(flagWidth, flagHeight, 5, 5);
      const flagMaterial = new THREE.MeshStandardMaterial({ 
        color: color,
        side: THREE.DoubleSide,
        roughness: 0.8,
        metalness: 0.1
      });
      const flag = new THREE.Mesh(flagGeometry, flagMaterial);
      flag.position.set(flagWidth / 2, flagpoleHeight - flagHeight / 2, 0);
      flag.rotation.y = Math.PI / 2;
      flag.castShadow = true;
      pole.add(flag);
      
      // Add some wave to the flag
      if (flag.geometry instanceof THREE.PlaneGeometry) {
        const positions = flag.geometry.attributes.position;
        for (let i = 0; i < positions.count; i++) {
          const x = positions.getX(i);
          const waveAmount = (x / flagWidth) * 0.2;
          positions.setZ(i, Math.sin(x * 5) * waveAmount);
        }
        positions.needsUpdate = true;
      }
    };
    
    // Add flags to towers
    const wallLength = 20;
    const halfWall = wallLength / 2;
    const towerHeight = 10;
    
    createFlag(-halfWall, towerHeight, -halfWall, 0xff0000); // Red flag
    createFlag(-halfWall, towerHeight, halfWall, 0x0000ff);  // Blue flag
    createFlag(halfWall, towerHeight, -halfWall, 0xffff00);  // Yellow flag
    createFlag(halfWall, towerHeight, halfWall, 0x00ff00);   // Green flag
    
    // Add a moat with water texture
    const moatWidth = 3;
    const moatDepth = 1;
    const moatOuterRadius = wallLength / 2 + moatWidth;
    const moatInnerRadius = wallLength / 2;
    
    const moatGeometry = new THREE.RingGeometry(moatInnerRadius, moatOuterRadius, 32);
    const moat = new THREE.Mesh(moatGeometry, waterMaterial);
    moat.rotation.x = -Math.PI / 2;
    moat.position.y = -moatDepth;
    parent.add(moat);
    
    // Add a drawbridge with wood texture
    const bridgeWidth = 4;
    const bridgeLength = moatWidth;
    const bridgeThickness = 0.2;
    
    const bridgeGeometry = new THREE.BoxGeometry(bridgeWidth, bridgeThickness, bridgeLength);
    
    // Adjust wood texture for bridge
    if (woodMaterial instanceof THREE.MeshStandardMaterial && woodMaterial.map) {
      const texture = woodMaterial.map.clone();
      texture.repeat.set(2, 1);
      texture.rotation = Math.PI / 2;
      
      const adjustedWoodMaterial = woodMaterial.clone();
      adjustedWoodMaterial.map = texture;
      
      const bridge = new THREE.Mesh(bridgeGeometry, adjustedWoodMaterial);
      bridge.position.set(0, -moatDepth + bridgeThickness / 2, -wallLength / 2 - bridgeLength / 2);
      bridge.castShadow = true;
      bridge.receiveShadow = true;
      parent.add(bridge);
    } else {
      // Fallback
      const bridge = new THREE.Mesh(bridgeGeometry, woodMaterial);
      bridge.position.set(0, -moatDepth + bridgeThickness / 2, -wallLength / 2 - bridgeLength / 2);
      bridge.castShadow = true;
      bridge.receiveShadow = true;
      parent.add(bridge);
    }
  }

  /**
   * Adds an object to the scene
   * @param object The object to add
   */
  public addObject(object: THREE.Object3D): void {
    this.scene.add(object);
    this.objects.push(object);
  }

  /**
   * Removes an object from the scene
   * @param object The object to remove
   */
  public removeObject(object: THREE.Object3D): void {
    const index = this.objects.indexOf(object);
    if (index !== -1) {
      this.objects.splice(index, 1);
      this.scene.remove(object);
    }
  }

  /**
   * Adds a callback function to be called on each update
   * @param callback The function to call on each update
   */
  public addUpdateCallback(callback: (deltaTime: number) => void): void {
    this.updateCallbacks.push(callback);
  }

  /**
   * Removes a callback function
   * @param callback The function to remove
   */
  public removeUpdateCallback(callback: (deltaTime: number) => void): void {
    const index = this.updateCallbacks.indexOf(callback);
    if (index !== -1) {
      this.updateCallbacks.splice(index, 1);
    }
  }

  /**
   * Updates all objects in the scene
   * @param deltaTime The time elapsed since the last update
   */
  public update(deltaTime: number): void {
    // Call all update callbacks
    for (const callback of this.updateCallbacks) {
      callback(deltaTime);
    }
  }

  /**
   * Cleans up resources when the manager is no longer needed
   */
  public dispose(): void {
    // Clear all objects
    for (const object of this.objects) {
      this.scene.remove(object);
      
      // Dispose of geometries and materials if they exist
      if (object instanceof THREE.Mesh) {
        if (object.geometry) {
          object.geometry.dispose();
        }
        
        if (object.material) {
          if (Array.isArray(object.material)) {
            for (const material of object.material) {
              material.dispose();
            }
          } else {
            object.material.dispose();
          }
        }
      }
    }
    
    this.objects = [];
    this.updateCallbacks = [];
  }
} 